import { useCallback, useEffect, useState } from "react";
import { calculateNewPosition, calculateNewSize } from "../utils";

export const useDragAndResize = (
  box,
  onUpdate,
  isAdmin,
  containerRef,
  onSelect
) => {
  const [isResizing, setIsResizing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [initialCoords, setInitialCoords] = useState(null);

  const handleMouseDown = useCallback(
    (e, action) => {
      if (!isAdmin) return;

      e.preventDefault();
      e.stopPropagation();

      const containerRect = containerRef.current?.getBoundingClientRect();
      if (!containerRect) return;

      setDragStart({ x: e.clientX, y: e.clientY });
      setInitialCoords({ ...box.boxCoords });

      if (action === "resize") {
        setIsResizing(true);
      } else if (action === "drag") {
        setIsDragging(true);
      }

      onSelect(box.id);
    },
    [isAdmin, box, onSelect, containerRef]
  );

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isAdmin || (!isDragging && !isResizing)) return;

      const containerRect = containerRef.current?.getBoundingClientRect();
      if (!containerRect || !initialCoords) return;

      const deltaX = ((e.clientX - dragStart.x) / containerRect.width) * 100;
      const deltaY = ((e.clientY - dragStart.y) / containerRect.height) * 100;

      if (isDragging) {
        const newCoords = calculateNewPosition(initialCoords, deltaX, deltaY);
        onUpdate(box.id, { boxCoords: newCoords });
      } else if (isResizing) {
        const newCoords = calculateNewSize(initialCoords, deltaX, deltaY);
        onUpdate(box.id, { boxCoords: newCoords });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
      setDragStart({ x: 0, y: 0 });
      setInitialCoords(null);
    };

    if (isDragging || isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [
    isDragging,
    isResizing,
    dragStart,
    initialCoords,
    box.id,
    onUpdate,
    isAdmin,
    containerRef,
  ]);

  return {
    isResizing,
    isDragging,
    handleMouseDown,
  };
};
